export type HostHubNuxConfigValue = {
  enabled?: boolean;
  expires?: string;
};

const storageKeyFor = (ssoId: string, nuxKey: string): string =>
  `nux:${ssoId}:${nuxKey}`;

export const hasViewedHostHubNux = (ssoId?: string, nuxKey?: string): boolean => {
  if (
    typeof window === "undefined" ||
    typeof localStorage === "undefined" ||
    !ssoId ||
    !nuxKey
  ) {
    return false;
  }

  // localStorage access can throw (private mode, blocked storage, quota).
  try {
    return localStorage.getItem(storageKeyFor(ssoId, nuxKey)) === "true";
  } catch {
    return false;
  }
};

export const markHostHubNuxViewed = (ssoId?: string, nuxKey?: string): void => {
  if (
    typeof window === "undefined" ||
    typeof localStorage === "undefined" ||
    !ssoId ||
    !nuxKey
  ) {
    return;
  }

  try {
    localStorage.setItem(storageKeyFor(ssoId, nuxKey), "true");
  } catch {
    // Ignore: failing to persist the viewed flag is non-critical.
  }
};

// Valid while `enabled` is true and, if `expires` is set, not yet past.
export const isNuxConfigValid = ({
  enabled = false,
  expires = "",
}: HostHubNuxConfigValue): boolean => {
  if (!enabled) {
    return false;
  }

  if (expires) {
    if (typeof expires !== "string") {
      return false;
    }

    const expiresDate = new Date(expires);

    if (Number.isNaN(expiresDate.getTime())) {
      return false;
    }

    if (new Date() >= expiresDate) {
      return false;
    }
  }

  return true;
};

export const isHostHubNuxEligible = ({
  isHost,
  nuxConfig,
  nuxKey,
  ssoId,
}: {
  isHost: boolean;
  nuxConfig: HostHubNuxConfigValue;
  nuxKey: string;
  ssoId?: string;
}): boolean => {
  if (!ssoId || !isHost) {
    return false;
  }

  if (hasViewedHostHubNux(ssoId, nuxKey)) {
    return false;
  }

  return isNuxConfigValid(nuxConfig);
};
